import type { FieldPath } from 'react-hook-form';

import {
  ActivityKindEnum,
  AdFieldCurrencyEnum,
  CompletionStatusEnum,
  RentPeriodEnum,
  TransactionTypeEnum,
} from '../../types/enums';
import type { PropertyAdEditorFormValues } from '../../types/propertyAdEditor';
import { hasValidDistressDiscount } from './propertyAdSale';

export interface PropertyAdPublishFieldError {
  field: FieldPath<PropertyAdEditorFormValues>;
  message: string;
}

const getRentPrice = (
  values: PropertyAdEditorFormValues,
  frequency: RentPeriodEnum
): number | null =>
  ({
    [RentPeriodEnum.Yearly]: values.priceYearly,
    [RentPeriodEnum.Monthly]: values.priceMonthly,
    [RentPeriodEnum.Weekly]: values.priceWeekly,
    [RentPeriodEnum.Daily]: values.priceDaily,
  })[frequency];

export const getPropertyAdPublishFieldErrors = (
  values: PropertyAdEditorFormValues
): PropertyAdPublishFieldError[] => {
  const errors: PropertyAdPublishFieldError[] = [];
  const addError = (field: FieldPath<PropertyAdEditorFormValues>, message: string) =>
    errors.push({ field, message });

  if (!values.activityKind) addError('activityKind', 'ActivityKind is a required field');

  if (values.activityKind === ActivityKindEnum.Sale && !values.transactionType) {
    addError('transactionType', 'Transaction Type is a required field');
  }

  if (!values.multilisting) addError('multilisting', 'Multilisting is a required field');

  if (values.activityKind === ActivityKindEnum.Sale) {
    const minimumPrice = values.currency === AdFieldCurrencyEnum.USD ? 2700 : 10000;

    if (values.price === null || values.price < minimumPrice) {
      addError(
        'price',
        values.price !== null && values.price > 0
          ? '10000AED'
          : 'Enter a numeric property price to continue'
      );
    }

    if (values.transactionType === TransactionTypeEnum.Distress) {
      if (values.sellingPrice === null || values.sellingPrice <= 0) {
        addError('sellingPrice', 'Enter a numeric Selling price to continue');
      } else if (!hasValidDistressDiscount(values.price, values.sellingPrice)) {
        addError('sellingPrice', '5%');
      }
    }

    const needsSellerPayment =
      values.transactionType === TransactionTypeEnum.Resale ||
      (values.transactionType === TransactionTypeEnum.Distress &&
        values.completionStatus === CompletionStatusEnum.OffPlan);

    if (needsSellerPayment && (!values.toSeller || values.toSeller <= 0)) {
      addError('toSeller', 'Enter a numeric');
    }
  }

  if (values.activityKind === ActivityKindEnum.Rent) {
    if (!values.defaultRentFrequency) {
      addError('defaultRentFrequency', 'Please fill in the rental period field');
    } else {
      const defaultPrice = getRentPrice(values, values.defaultRentFrequency);
      if (defaultPrice === null || defaultPrice <= 0) {
        const priceField = {
          [RentPeriodEnum.Yearly]: 'priceYearly',
          [RentPeriodEnum.Monthly]: 'priceMonthly',
          [RentPeriodEnum.Weekly]: 'priceWeekly',
          [RentPeriodEnum.Daily]: 'priceDaily',
        }[values.defaultRentFrequency] as FieldPath<PropertyAdEditorFormValues>;
        addError(priceField, 'Enter a numeric property price to continue');
      }
    }
  }

  if (!values.locationId) addError('locationId', 'Location must be confirmed before publishing');

  return errors;
};
